// TODO: TS: check ts

import type { PaginatedResponse } from "features/shared/model/types";

import axios, { type AxiosProgressEvent, type AxiosRequestConfig, type AxiosResponse } from "axios";
import { toast } from "react-toastify";

import { store } from "../store";
import { getUserFromLocalStorage, removeUserFromLocalStorage } from "./localStorage";

const API_RESULT_LARGE_LIMIT = 100000;
const API_RESULT_MEDIUM_LIMIT = 1000;

type Headers = {
  Authorization?: string;
  "X-Tenant-Domain"?: string;
};

const addTokenToHeaders = (headers: Headers = {}, useToken = true): Headers => {
  const user = getUserFromLocalStorage() as null | { access_token?: string };
  if (user && useToken) headers["Authorization"] = `Bearer ${user.access_token}`;
  return headers;
};

const handleRequestError = (error: any): any => {
  if (error?.response?.status === 401) {
    const currentUser = getUserFromLocalStorage();
    if (currentUser?.user_id) toast.info("You have been logged out due to token expiration for your security.");
    clearStoreAndLocalStorage();
  }
  throw error;
};

export const customRequestErrorHandler = ({
  error,
  thunkAPI,
}: {
  callbacks?: any;
  error: any;
  thunkAPI: { rejectWithValue: (value: any) => any };
}) => {
  return thunkAPI.rejectWithValue(error.response.data.error);
};

export const clearStoreAndLocalStorage = () => {
  removeUserFromLocalStorage();
  store.dispatch({ type: "unauthorizedLogout" });
};

type RequestType = "delete" | "get" | "head" | "patch" | "post" | "put";

const apiRequest = async (
  props: any[],
  requestType: RequestType,
  useToken = true,
  headers: Headers = {}
): Promise<AxiosResponse<any>> => {
  try {
    headers = addTokenToHeaders(headers, useToken);
    const config: AxiosRequestConfig = { headers };
    const fn = (axios as any)[requestType];
    const resp = await fn(...props, config);
    return resp;
  } catch (error) {
    return handleRequestError(error);
  }
};

type CommonOpts = { headers?: Headers; useToken?: boolean };
type DataOpts = CommonOpts & { data?: any; url: string };
type DeleteResult = {
  index: number;
  reason?: unknown;
  status: "fulfilled" | "rejected";
  url: string;
  value?: unknown;
};

type UrlOpts = CommonOpts & { headers?: Headers; url: string };

export const customApiCall = {
  delete: async function ({ headers = {}, url, useToken = true }: UrlOpts) {
    const props = [url];
    const resp = await apiRequest(props, "delete", useToken, headers);
    return resp.data;
  },
  deleteAll: function ({
    headers = {},
    onEach,
    urls,
    useToken = true,
  }: CommonOpts & { onEach?: (r: DeleteResult) => void; urls: string[] }) {
    if (!urls?.length) return [];
    return urls.map((url, index) => {
      const props = [url];
      const p = apiRequest(props, "delete", useToken, headers).then((resp) => resp.data);
      p.then(
        (value) => onEach?.({ index, status: "fulfilled", url, value }),
        (reason) => onEach?.({ index, reason, status: "rejected", url })
      );
      return p;
    });
  },
  download: async function ({
    errorMessage,
    fileName,
    headers = {},
    url,
    useToken = true,
  }: CommonOpts & { errorMessage?: string; fileName: string; url: string }) {
    const user = getUserFromLocalStorage() as null | { access_token?: string };
    if (user && useToken) {
      headers["Authorization"] = `Bearer ${user.access_token}`;
    }
    const config: AxiosRequestConfig = { headers, responseType: "blob" };
    axios
      .get(url, config)
      .then((response) => {
        const result = new Blob([response.data]);
        const objectUrl = window.URL.createObjectURL(result);
        const link = document.createElement("a");
        link.href = objectUrl;
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
      })
      .catch(() => {
        if (errorMessage) toast.error(errorMessage);
      });
  },
  get: async function ({ headers = {}, url, useToken = true }: UrlOpts) {
    const props = [url];
    const resp = await apiRequest(props, "get", useToken, headers);
    return resp.data;
  },
  head: async function ({ url, useToken = true }: UrlOpts) {
    const props = [url];
    const resp = await apiRequest(props, "head", useToken);
    return resp;
  },
  patch: async function ({ data, url, useToken = true }: DataOpts) {
    const props = [url, data];
    const resp = await apiRequest(props, "patch", useToken);
    return resp.data;
  },
  post: async function ({ data, headers = {}, url, useToken = true }: DataOpts) {
    const props = [url, data];
    const resp = await apiRequest(props, "post", useToken, headers);
    return resp.data;
  },
  put: async function ({ data, url, useToken = true }: DataOpts) {
    const props = [url, data];
    const resp = await apiRequest(props, "put", useToken);
    return resp.data;
  },
  upload: async function ({
    data,
    headers = {},
    onUploadProgress,
    signal,
    url,
    useToken = true,
  }: CommonOpts & {
    data: Record<string, any>;
    onUploadProgress?: (event: { percentage: number }) => void;
    signal?: AbortSignal;
    url: string;
  }) {
    try {
      const formData = new FormData();
      for (const [key, value] of Object.entries(data)) {
        if (value !== undefined && value !== null) {
          formData.append(key, value as Blob | string);
        }
      }
      headers = addTokenToHeaders(headers, useToken);
      const resp = await axios.post(url, formData, {
        headers,
        onUploadProgress: onUploadProgress
          ? (progressEvent: AxiosProgressEvent) => {
              const total = progressEvent.total ?? 0;
              const percentage = total > 0 ? Math.min(100, Math.round((progressEvent.loaded * 100) / total)) : 0;
              onUploadProgress({ percentage });
            }
          : undefined,
        signal,
      });
      return resp.data;
    } catch (error) {
      return handleRequestError(error);
    }
  },
};

const singlePageApiCall = async function <T>(props: { limit?: number; page?: number; url: string }) {
  try {
    let { url } = props;
    const endpointPagePart = `page=${props.page ?? 1}&limit=${props.limit ?? API_RESULT_MEDIUM_LIMIT}`;

    if (!url.includes("?")) url += `?${endpointPagePart}`;
    else url += `&${endpointPagePart}`;

    return (await customApiCall.get({ ...props, url })) as PaginatedResponse<T>;
  } catch {
    return { currentPage: 1, limit: 0, list: [], maxItems: 0, pages: 1 } as PaginatedResponse<T>;
  }
};

export const customPaginatedApi1PageNoLimitCall = {
  get: async function <T>(props: { url: string }) {
    const result = await singlePageApiCall<T>({ limit: API_RESULT_LARGE_LIMIT, ...props });
    return result?.list ?? [];
  },
};

export const customPaginatedAllPagesFetchCall = {
  get: async function <T>(props: { url: string }) {
    try {
      const allPages: T[] = [];

      let nextPage = 1;
      let pages = 1;

      while (nextPage <= pages) {
        const resp = await singlePageApiCall<T>({ ...props, page: nextPage });
        if (resp?.list?.length > 0) {
          allPages.push(...resp.list);
        }
        pages = resp?.pages ?? 1;
        nextPage++;
      }

      return allPages;
    } catch {
      return [];
    }
  },
};

export const customPaginatedAllPagesFetchCallParallel = {
  get: async function <T>(props: { url: string }) {
    try {
      const firstPage = await singlePageApiCall<T>({ ...props, page: 1 });
      const pages = firstPage?.pages ?? 1;
      const allPages: T[] = firstPage?.list?.length > 0 ? [...firstPage.list] : [];

      if (pages <= 1) return allPages;

      const remainingPages = await Promise.all(
        Array.from({ length: pages - 1 }, (_, i) => singlePageApiCall<T>({ ...props, page: i + 2 }))
      );

      for (const resp of remainingPages) {
        if (resp?.list?.length > 0) {
          allPages.push(...resp.list);
        }
      }

      return allPages;
    } catch {
      return [];
    }
  },
};
